import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api/axios";
import { Link } from "react-router-dom";
import { useActivityLogger } from "./hooks/useActivity";
import { getImageUrl } from "./utils/image";
import { getRandomThumbnail } from "./utils/randomImage";

export default function CariArtikel() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Update state untuk menyimpan ID kategori
  const [selectedCategoryId, setSelectedCategoryId] = useState(""); 
  
  // State untuk list kategori dari API
  const [categories, setCategories] = useState([]);

  const { logUserActivity, loading: userLoading } = useActivityLogger();

  // 1. Fetch Categories dari API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get("/kategori");
        setCategories(res.data);
      } catch (err) {
        console.error("Gagal mengambil kategori:", err);
      }
    };
    fetchCategories();
  }, []);

  // 2. Fetch Articles dan Filter
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        let response;
        if (searchTerm.trim()) {
          response = await api.get(
            `/articles/search?title=${encodeURIComponent(searchTerm)}`
          );
        } else {
          response = await api.get("/articles/all");
        }

        let filteredArticles = response.data || [];

        // Filter by category ID
        if (selectedCategoryId) {
          filteredArticles = filteredArticles.filter(
            (video) => 
              // Convert selectedCategoryId to number for comparison
              (video.category?.ID === parseInt(selectedCategoryId)) || 
              (video.category_id === parseInt(selectedCategoryId))
          );  
        }

        // LOGIKA SORTING
        filteredArticles.sort((a, b) => {
          // Konversi published_date ke timestamp
          const pubDateA = a.published_date
            ? new Date(a.published_date).getTime()
            : null;
          const pubDateB = b.published_date
            ? new Date(b.published_date).getTime()
            : null;

          // Cek validitas tanggal (tidak null dan bukan NaN)
          const hasPubA = pubDateA && !isNaN(pubDateA);
          const hasPubB = pubDateB && !isNaN(pubDateB);

          // 1. PISAHKAN YANG PUNYA PUBLISHED DATE VS TIDAK
          if (hasPubA && !hasPubB) return -1;
          if (!hasPubA && hasPubB) return 1;

          // 2. JIKA KEDUANYA PUNYA PUBLISHED DATE
          if (hasPubA && hasPubB) {
            return pubDateB - pubDateA;
          }

          // 3. JIKA KEDUANYA TIDAK PUNYA PUBLISHED DATE
          const createdA = new Date(a.CreatedAt || a.created_at || 0).getTime();
          const createdB = new Date(b.CreatedAt || b.created_at || 0).getTime();

          return createdB - createdA;
        });

        console.log("Fetched sorted articles:", filteredArticles);
        setArticles(filteredArticles);

        if (!userLoading) {
          logUserActivity(
            "user",
            "view_articles",
            "CariArtikel",
            `User viewed articles with search term: ${searchTerm}, category ID: ${selectedCategoryId}`
          );
        }
      } catch (error) {
        console.error("Failed to fetch articles:", error);
        if (!userLoading) {
          logUserActivity(
            "error",
            "fetch_articles_error",
            "CariArtikel",
            `Error fetching articles: ${error.message}`
          );
        }
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [searchTerm, selectedCategoryId, userLoading]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "Tanggal tidak diketahui";

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "1 hari yang lalu";
    if (diffDays < 30) return `${diffDays} hari yang lalu`;
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} bulan yang lalu`;
    }
    const years = Math.floor(diffDays / 365);
    return `${years} tahun yang lalu`;
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategoryId(e.target.value);
  };

  const handleArticleClick = (article) => {
    console.log("Article clicked:", article);
    logUserActivity(
      "user",
      "view_article",
      "CariArtikel",
      `User viewed article: ${article.title || article.id || article.ID}`
    );
    if (article.id || article.ID) {
      navigate(`/article/${article.id || article.ID}`);
    } else {
      console.warn("No ID found for article:", article);
    }
  };

  // Helper untuk menampilkan nama kategori di chip filter
  const getCategoryNameById = (id) => {
    const cat = categories.find(c => c.ID === parseInt(id));
    return cat ? cat.nama_kategori : "Unknown";
  };

  return (
    <div className="min-h-screen bg-white px-4 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => {
            logUserActivity(
              "navigation",
              "go_back_from_search",
              "CariArtikel",
              "User clicked back from search"
            );
            navigate("/");
          }}
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-semibold">Artikel</h1>
        <button>
          {/* <img src="/icon-bookmark.png" alt="Bookmark" className="h-6 w-6" /> */}
        </button>
      </div>

      {/* Search Bar and Category Filter */}
      <div className="flex items-center space-x-3 mb-4">
        {/* Search Bar */}
        <div className="relative flex-[2]">
          <img
            src="/icon-search.png"
            alt="Search"
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
          />
          <input
            type="text"
            placeholder="Masukkan kata kunci..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-sm"
          />
        </div>

        {/* Category Filter Dropdown */}
        <div className="relative flex-1">
          <select
            value={selectedCategoryId}
            onChange={handleCategoryChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Semua Kategori</option>
            {categories.map((category) => (
              <option key={category.ID} value={category.ID}>
                {category.nama_kategori}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Active Filters Display */}
      {(searchTerm || selectedCategoryId) && (
        <div className="mb-4 flex flex-wrap gap-2">
          {searchTerm && (
            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
              Pencarian: "{searchTerm}"
              <button
                onClick={() => setSearchTerm("")}
                className="text-blue-600 hover:text-blue-800"
              >
                ×
              </button>
            </span>
          )}
          {selectedCategoryId && (
            <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
              Kategori: {getCategoryNameById(selectedCategoryId)}
              <button
                onClick={() => setSelectedCategoryId("")}
                className="text-green-600 hover:text-green-800"
              >
                ×
              </button>
            </span>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8 text-gray-500">Memuat artikel...</div>
      )}

      {/* Article List */}
      {!loading && (
        <div className="space-y-4">
          {articles.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              {searchTerm || selectedCategoryId
                ? "Tidak ada artikel ditemukan dengan filter tersebut."
                : "Belum ada artikel."}
            </div>
          ) : (
            articles.map((article) => (
              <Link
                key={article.id || article.ID || article.url}
                onClick={() => handleArticleClick(article)}
                className="flex gap-3 w-full text-left hover:bg-gray-50 p-3 rounded-lg transition cursor-pointer active:bg-gray-100"
                to={`/article/${article.id || article.ID || article.url}`}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleArticleClick(article);
                  }
                }}
              >
                <div className="relative w-40 h-24 shrink-0 bg-white items-center justify-center flex">
                  <img
                    src={
                      article.image
                        ? getImageUrl(article.image)
                        : getRandomThumbnail(article.title)
                    }
                    className="object-contain h-24 rounded-md items-center justify-center"
                    alt={article.title || `Artikel ${article.id}`}
                    style={{ background: "#fff" }}
                    onError={(e) => {
                      e.target.src =
                        article.id === 1 ? "/artikel1.png" : "/artikel2.png";
                    }}
                  />
                </div>
                <div className="text-sm flex-1 min-w-0">
                  <p className="font-medium mb-1 line-clamp-2 text-gray-900">
                    {article.title}
                  </p>
                  <p className="text-gray-500 mb-1">
                    {article.author || article.source || "Unknown Source"}
                  </p>
                  <p className="text-gray-400 text-xs mb-2">
                    {formatDate(article.published_date || article.createdAt)}
                  </p>
                  {/* Display category name from relation if available */}
                  {article.category && (
                    <p className="text-xs text-blue-600 bg-blue-50 inline-block px-2 py-1 rounded">
                      {article.category.nama_kategori}
                    </p>
                  )}
                </div>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}